import React, { type ReactElement } from 'react';
import { faPersonRunning, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { ConditionalTooltipItem } from 'atom-components/tooltip-item';
import { friendlyMarkerName } from 'helpers/domain-specific/marker.helpers';
import type { PartialOrganisationUserWithPartialUser } from 'services/assist-api/entities/customer-user/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import styles from './base-marker-badge.module.scss';

export type PartialTestInstanceResponseMarkerWithCustomerUser = (
  | { organisationUser: PartialOrganisationUserWithPartialUser }
  | PartialOrganisationUserWithPartialUser
) &
  Pick<TestInstanceResponseMarkerWithCustomerUser, 'id'>;

type MarkerDetails = {
  activeMarker: PartialTestInstanceResponseMarkerWithCustomerUser | undefined;
  defaultMarkerToOrganisationUser?: boolean;
  organisationUser: PartialOrganisationUserWithPartialUser | undefined;
  autoscored: boolean;
  omitTooltip?: boolean;
};

type MarkerBadgeProps = { displayWarning: boolean } & MarkerDetails;

export const autoscoredText = 'Autoscored';
export const noActiveMarkerFoundText = 'No Active Marker Found';

function displayName(markerDetails: MarkerDetails): string {
  const { activeMarker, defaultMarkerToOrganisationUser, organisationUser, autoscored } =
    markerDetails;
  if (autoscored) return autoscoredText;
  if (activeMarker || defaultMarkerToOrganisationUser) return friendlyMarkerName(organisationUser);
  return noActiveMarkerFoundText;
}

/**
 * The base badge for displaying marker details. Generally, one of the wrapper marker badges
 * that automatically set up the props should be used instead of using this badge directly.
 */
export default function BaseMarkerBadge({
  displayWarning,
  activeMarker,
  defaultMarkerToOrganisationUser,
  organisationUser,
  autoscored,
  omitTooltip
}: MarkerBadgeProps): ReactElement {
  const markerDetails = {
    activeMarker,
    defaultMarkerToOrganisationUser,
    organisationUser,
    autoscored
  };

  const humanMarkerUsername =
    organisationUser?.user.username === undefined
      ? 'No Username'
      : `@${organisationUser?.user.username}`;
  const tooltip = autoscored ? 'Autoscored' : humanMarkerUsername;

  return (
    <ConditionalTooltipItem showTooltip={!omitTooltip} tooltip={tooltip}>
      <span
        className={clsx(
          'badge rounded-pill',
          { 'text-bg-success': !displayWarning && !autoscored },
          { 'text-bg-warning': displayWarning && !autoscored },
          {
            'text-bg-danger':
              ((!activeMarker && !defaultMarkerToOrganisationUser) || !organisationUser?.id) &&
              !autoscored
          },
          { 'text-bg-info': autoscored }
        )}
      >
        <FontAwesomeIcon
          icon={autoscored ? faRobot : faPersonRunning}
          className={styles.markerIcon}
        />{' '}
        <span className={styles.marker}>{displayName(markerDetails)}</span>
      </span>
    </ConditionalTooltipItem>
  );
}
